
import { supabase } from './client';

// Define result types
export interface SetupResult {
  success: boolean;
  alreadyConfigured?: boolean;
  error?: string;
}

// Check if RLS is properly configured for our tables
export const checkRlsStatus = async (): Promise<{enabled: boolean, policies: any[]}> => {
  try {
    // Check if policies exist in pg_policies for bookings and subscriptions tables
    const { data: policiesData, error: policiesError } = await supabase
      .from('pg_policies')
      .select('*')
      .in('tablename', ['bookings', 'subscriptions']);
    
    if (policiesError) {
      console.error('Error fetching policies:', policiesError);
      throw new Error(`Error fetching policies: ${policiesError.message}`);
    }
    
    return { 
      enabled: policiesData && policiesData.length > 0, 
      policies: policiesData || []
    };
  } catch (error: any) {
    console.error('Error checking RLS status:', error);
    return { enabled: false, policies: [] };
  }
};

// Helper function to run directly in browser console
export const setupDatabaseSecurity = async (): Promise<SetupResult> => {
  console.log('Checking current RLS status for tables...');
  const rlsStatus = await checkRlsStatus();
  
  console.log('RLS Status:', rlsStatus);
  
  if (!rlsStatus.enabled || rlsStatus.policies.length < 16) {
    console.log('Setting up RLS policies...');
    
    // Try using the direct pg_query function
    try {
      // Enable RLS on the tables
      const enableRlsBookings = await supabase.rpc('pg_query', { 
        query: `ALTER TABLE public.bookings ENABLE ROW LEVEL SECURITY;`
      });
      
      const enableRlsSubscriptions = await supabase.rpc('pg_query', { 
        query: `ALTER TABLE public.subscriptions ENABLE ROW LEVEL SECURITY;`
      });
      
      if (enableRlsBookings.error) {
        console.error('Error enabling RLS on bookings:', enableRlsBookings.error);
      }
      
      if (enableRlsSubscriptions.error) {
        console.error('Error enabling RLS on subscriptions:', enableRlsSubscriptions.error);
      }
      
      // Create essential policies
      const bookingUserPolicies = [
        `CREATE POLICY IF NOT EXISTS "Users can view their own bookings" 
         ON public.bookings FOR SELECT USING (auth.uid() = user_id);`,
        
        `CREATE POLICY IF NOT EXISTS "Users can insert their own bookings" 
         ON public.bookings FOR INSERT WITH CHECK (auth.uid() = user_id);`,
        
        `CREATE POLICY IF NOT EXISTS "Users can update their own bookings" 
         ON public.bookings FOR UPDATE USING (auth.uid() = user_id);`,
        
        `CREATE POLICY IF NOT EXISTS "Users can delete their own bookings" 
         ON public.bookings FOR DELETE USING (auth.uid() = user_id);`
      ];
      
      const subscriptionUserPolicies = [
        `CREATE POLICY IF NOT EXISTS "Users can view their own subscriptions" 
         ON public.subscriptions FOR SELECT USING (auth.uid() = user_id);`,
        
        `CREATE POLICY IF NOT EXISTS "Users can insert their own subscriptions" 
         ON public.subscriptions FOR INSERT WITH CHECK (auth.uid() = user_id);`,
        
        `CREATE POLICY IF NOT EXISTS "Users can update their own subscriptions" 
         ON public.subscriptions FOR UPDATE USING (auth.uid() = user_id);`,
        
        `CREATE POLICY IF NOT EXISTS "Users can delete their own subscriptions" 
         ON public.subscriptions FOR DELETE USING (auth.uid() = user_id);`
      ];
      
      const bookingAdminPolicies = [
        `CREATE POLICY IF NOT EXISTS "Admins can view all bookings" 
         ON public.bookings FOR SELECT USING (public.is_admin());`,
        
        `CREATE POLICY IF NOT EXISTS "Admins can update all bookings" 
         ON public.bookings FOR UPDATE USING (public.is_admin());`,
        
        `CREATE POLICY IF NOT EXISTS "Admins can delete all bookings" 
         ON public.bookings FOR DELETE USING (public.is_admin());`,
        
        `CREATE POLICY IF NOT EXISTS "Admins can insert bookings" 
         ON public.bookings FOR INSERT WITH CHECK (public.is_admin());`
      ];
      
      const subscriptionAdminPolicies = [
        `CREATE POLICY IF NOT EXISTS "Admins can view all subscriptions" 
         ON public.subscriptions FOR SELECT USING (public.is_admin());`,
        
        `CREATE POLICY IF NOT EXISTS "Admins can insert all subscriptions" 
         ON public.subscriptions FOR INSERT WITH CHECK (public.is_admin());`,
        
        `CREATE POLICY IF NOT EXISTS "Admins can update all subscriptions" 
         ON public.subscriptions FOR UPDATE USING (public.is_admin());`,
        
        `CREATE POLICY IF NOT EXISTS "Admins can delete all subscriptions" 
         ON public.subscriptions FOR DELETE USING (public.is_admin());`
      ];
      
      const anonymousPolicy = 
        `CREATE POLICY IF NOT EXISTS "Allow anonymous bookings" 
         ON public.bookings FOR INSERT WITH CHECK (user_id IS NULL);`;
      
      // Execute all policies
      for (const sql of [
        ...bookingUserPolicies, 
        ...subscriptionUserPolicies, 
        ...bookingAdminPolicies, 
        ...subscriptionAdminPolicies, 
        anonymousPolicy
      ]) {
        const { error } = await supabase.rpc('pg_query', { query: sql });
        if (error && !error.message.includes('already exists')) {
          console.error(`Error creating policy: ${sql}`, error);
        }
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error setting up RLS policies:', error);
      return { success: false, error: error.message };
    }
  } else {
    console.log('RLS is already properly configured.');
    return { success: true, alreadyConfigured: true };
  }
};

// Add this function to test RLS access
export const testBookingsRLS = async () => {
  const { data: session } = await supabase.auth.getSession();
  const currentUserId = session?.session?.user?.id;
  
  console.log('Testing RLS access for bookings table...');
  console.log('Current user ID:', currentUserId || 'Not logged in');
  
  // Test insert operation
  const { data: insertData, error: insertError } = await supabase
    .from('bookings')
    .insert({
      class_id: '00000000-0000-0000-0000-000000000000', // Test UUID
      first_name: 'Test',
      last_name: 'User',
      email: 'test@example.com',
      phone: '1234567890'
      // user_id will be automatically set to the current user's ID by RLS
    })
    .select();
  
  console.log('Insert test:', {
    success: !insertError,
    data: insertData,
    error: insertError
  });
  
  // Test select operation
  const { data: selectData, error: selectError } = await supabase
    .from('bookings')
    .select('*')
    .limit(5);
  
  console.log('Select test:', {
    success: !selectError,
    count: selectData?.length || 0,
    error: selectError
  });
  
  return {
    insert: { success: !insertError, error: insertError?.message },
    select: { success: !selectError, count: selectData?.length || 0, error: selectError?.message }
  };
};

// Make it globally accessible for browser console usage
if (typeof window !== 'undefined') {
  (window as any).setupDatabaseSecurity = setupDatabaseSecurity;
  (window as any).testBookingsRLS = testBookingsRLS;
}

export default setupDatabaseSecurity;
